import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Button, Modal, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { MotionType, Scene, SceneAssetItem } from '@monorepo/types';
import { MotionGroupList } from './motion-group-list';
import { useVideoContext } from '../../../../../../../../hooks/use-video';

export interface MotionModalActions {
  open: (scene: Scene) => void;
}

const MotionSelectModal = forwardRef<MotionModalActions>((_, ref) => {
  const { t } = useTranslation();
  const [currentMotion, setCurrentMotion] = useState<MotionType>(
    MotionType.NO_MOTION
  );
  const [isOpen, setIsOpen] = useState(false);

  const {
    updateSceneItem,
    assets,
    getSceneStartFrame,
    selectedScene: currentScene,
    selectedSceneItemIndex,
  } = useVideoContext();

  useImperativeHandle(ref, () => ({
    open: (scene: Scene) => {
      setIsOpen(true);
    },
  }));

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleSave = async () => {
    if (!currentScene) return;

    const frame = getSceneStartFrame(currentScene._id);

    await updateSceneItem({
      sceneId: currentScene._id,
      videoId: currentScene.videoId,
      itemIndex: selectedSceneItemIndex,
      updateDto: {
        data: { motion: currentMotion },
      },
    });

    handleCancel();
  };

  const item = currentScene?.items[selectedSceneItemIndex];
  const assetData = item?.data as SceneAssetItem;
  const sceneAsset = assets.find((asset) => asset._id === assetData.assetId);

  useEffect(() => {
    if (sceneAsset) {
      setCurrentMotion(assetData.motion);
    }
  }, [currentScene, selectedSceneItemIndex]);

  if (!currentScene) {
    return null;
  }

  return (
    <Modal
      title={t('videoEditor.motionModal.title')}
      open={isOpen}
      onCancel={handleCancel}
      width={1100}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          {t('videoEditor.motionModal.saveChanges')}
        </Button>,
      ]}
    >
      <Space
        direction="vertical"
        className={'scroller'}
        style={{
          width: '100%',
          maxHeight: '500px',
          padding: '5px',
          overflowY: 'scroll',
        }}
      >
        {sceneAsset ? (
          <MotionGroupList
            selectedMotion={currentMotion}
            onSelect={(motion) => {
              setCurrentMotion(motion);
            }}
            imageUrl={sceneAsset.url}
          />
        ) : null}
      </Space>
    </Modal>
  );
});

export default MotionSelectModal;
